import { HttpErrorResponse } from "@angular/common/module.d-CnjH8Dlt";
import { SharedActions } from "../states/shared/shared.action-types";
import { UtilsService } from "../../edge/utils.service";
import { Actions, createEffect, ofType } from "@ngrx/effects";
import { inject } from "@angular/core";
import { catchError, exhaustMap, share, switchMap } from "rxjs";
import { NavigationTreeActions } from "../states/navigation-tree/navigation-tree.actions";
import { NavigationTreeService } from "../../services/navigation-tree.service";
import { NavigationTreeModels } from "../models/navigation-tree.model";
import { select, Store } from "@ngrx/store";
import { concatLatestFrom } from "@ngrx/operators";
import { navigationTreeFeature } from "../states/navigation-tree/navigation-tree.feature";

export const saveNavigationOrderKeysToStore$ = createEffect(
    (
        actions$ = inject(Actions),
        store = inject(Store),
    ) =>
        actions$.pipe(
            ofType(NavigationTreeActions.saveOrderKeysToStore),
            concatLatestFrom(() => [
                store.pipe(select(navigationTreeFeature.selectNavigationIdOrderKey)),
            ]),
            exhaustMap(([{ orderedIds, idTypeMap, shiftIndex }, currentOrdering]) => {
                const idOrderKey: Record<string, number> = { ...currentOrdering };

                orderedIds.forEach((id, orderKey) => {
                    const type = idTypeMap[id];
                    switch (type) {
                        case NavigationTreeModels.NodeType.LOCATION:
                        case NavigationTreeModels.NodeType.SUB_LOCATION:
                        case NavigationTreeModels.NodeType.TAG:
                        case NavigationTreeModels.NodeType.WALL:
                            idOrderKey[id] = orderKey;
                            break;
                        default:
                            idOrderKey[id] = orderKey - shiftIndex;
                    }
                });

                return [
                    NavigationTreeActions.getNavigationTreeOrderingSuccess({ documents: idOrderKey }),
                ];
            }),
            share(),
        ),
    { functional: true },
);

export const saveNavigationOrderKeys$ = createEffect(
    (
        actions$ = inject(Actions),
        navigationTreeService = inject(NavigationTreeService),
    ) =>
        actions$.pipe(
            ofType(NavigationTreeActions.saveOrderKeys),
            exhaustMap(({ orderedIds, shiftIndex, idTypeMap }) => {
                const idOrderKey: Record<string, number> = {};

                orderedIds.forEach((id, orderKey) => {
                    const type = idTypeMap[id];
                    switch (type) {
                        case NavigationTreeModels.NodeType.LOCATION:
                        case NavigationTreeModels.NodeType.SUB_LOCATION:
                        case NavigationTreeModels.NodeType.TAG:
                        case NavigationTreeModels.NodeType.WALL:
                            idOrderKey[id] = orderKey;
                            break;
                        default:
                            idOrderKey[id] = orderKey - shiftIndex;
                    }
                });

                return navigationTreeService
                    .updateNavigationOrdering(idOrderKey)
                    .pipe(
                        switchMap(res => {
                            return [
                                NavigationTreeActions.saveOrderKeysSuccess(),
                                SharedActions.showMessage({ success: 'Order has been saved' }),
                            ];
                        }),
                        catchError(response => {
                            return [
                                NavigationTreeActions.saveOrderKeysFail(),
                            ];
                        }),
                    );
            }),
            share(),
        ),
    { functional: true },
);

export const getNavigationTreeOrdering$ = createEffect(
    (
        actions$ = inject(Actions),
        navigationTreeService = inject(NavigationTreeService),
    ) =>
        actions$.pipe(
            ofType(NavigationTreeActions.getNavigationTreeOrdering),
            exhaustMap(() => {
                return navigationTreeService
                    .getNavigationOrdering()
                    .pipe(
                        switchMap(res => {
                            const toSave: Record<string, number> = {};

                            res.forEach(item => {
                                toSave[item.itemId] = item.orderKey;
                            });

                            return [
                                NavigationTreeActions.getNavigationTreeOrderingSuccess({ documents: toSave }),
                            ];
                        }),
                        catchError(response => {
                            return [
                                NavigationTreeActions.getNavigationTreeOrderingFail(),
                            ];
                        }),
                    );
            }),
            share(),
        ),
    { functional: true },
);